import { createTheme, alpha } from '@mui/material/styles';

import { CustomShadowProps } from '@/@mantis/types/theme';

declare module '@mui/material/styles' {
  interface TypographyVariantsOptions {
    landingH1?: React.CSSProperties;
    landingH2?: React.CSSProperties;
    landingH3?: React.CSSProperties;
    landingSubtitle?: React.CSSProperties;
    landingBody?: React.CSSProperties;
  }

  interface TypographyVariants {
    landingH1: React.CSSProperties;
    landingH2: React.CSSProperties;
    landingH3: React.CSSProperties;
    landingSubtitle: React.CSSProperties;
    landingBody: React.CSSProperties;
  }

  interface Palette {
    landingPage: {
      primary: string;
      secondary: string;
      background: string;
      textPrimary: string;
      textSecondary: string;
      accent: string;
    };
  }

  interface PaletteOptions {
    landingPage?: {
      primary?: string;
      secondary?: string;
      background?: string;
      textPrimary?: string;
      textSecondary?: string;
      accent?: string;
    };
  }

  interface Theme {
    customShadows: CustomShadowProps;
  }

  interface ThemeOptions {
    customShadows?: CustomShadowProps;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    landingH1: true;
    landingH2: true;
    landingH3: true;
    landingSubtitle: true;
    landingBody: true;
  }
}

// Extract typography from Figma design
const typography = {
  fontFamily: '"Inter", "Nohemi", "Roboto", "Arial", sans-serif',
  landingH1: {
    fontFamily: '"Nohemi", sans-serif',
    fontWeight: 600,
    fontSize: '64px',
    lineHeight: 1.2,
    letterSpacing: '0.01em',
  },
  landingH2: {
    fontFamily: '"Nohemi", sans-serif',
    fontWeight: 600,
    fontSize: '36px',
    lineHeight: 1.22,
    letterSpacing: '0.02em',
  },
  landingH3: {
    fontFamily: '"Nohemi", sans-serif',
    fontWeight: 500,
    fontSize: '30px',
    lineHeight: 1.27,
    letterSpacing: '0.02em',
  },
  landingSubtitle: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: 1.5,
  },
  landingBody: {
    fontFamily: '"Inter", sans-serif',
    fontWeight: 400,
    fontSize: '18px',
    lineHeight: 1.56,
  },
};

// Define custom shadows
const customShadows: CustomShadowProps = {
  button: '0 2px #0000000b',
  text: '0 -1px 0 rgb(0 0 0 / 12%)',
  z1: `0px 1px 4px ${alpha('#000000', 0.08)}`,
  primary: `0 0 0 2px ${alpha('#9D1500', 0.2)}`,
  primaryButton: `0 14px 12px ${alpha('#9D1500', 0.2)}`,
  secondary: `0 0 0 2px ${alpha('#414651', 0.2)}`,
  secondaryButton: `0 14px 12px ${alpha('#414651', 0.2)}`,
  error: `0 0 0 2px ${alpha('#f44336', 0.2)}`,
  errorButton: `0 14px 12px ${alpha('#f44336', 0.2)}`,
  warning: `0 0 0 2px ${alpha('#ff9800', 0.2)}`,
  warningButton: `0 14px 12px ${alpha('#ff9800', 0.2)}`,
  info: `0 0 0 2px ${alpha('#2196f3', 0.2)}`,
  infoButton: `0 14px 12px ${alpha('#2196f3', 0.2)}`,
  success: `0 0 0 2px ${alpha('#4caf50', 0.2)}`,
  successButton: `0 14px 12px ${alpha('#4caf50', 0.2)}`,
  grey: `0 0 0 2px ${alpha('#9e9e9e', 0.2)}`,
  greyButton: `0 14px 12px ${alpha('#9e9e9e', 0.2)}`,
};

// Extract colors from Figma design
const landingPageTheme = createTheme({
  typography,
  palette: {
    landingPage: {
      primary: '#FFFFFF',
      secondary: '#F5F5F5',
      background: '#FFFFFF',
      textPrimary: '#181D27',
      textSecondary: '#535862',
      accent: '#9D1500',
    },
    primary: {
      main: '#9D1500',
      light: '#FFE7E3',
      dark: '#7A1100',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#414651',
      light: '#F5F5F5',
      dark: '#181D27',
      contrastText: '#FFFFFF',
    },
  },
  shape: {
    borderRadius: 10,
  },
  customShadows,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '16px',
          padding: '12px 22px',
        },
        containedPrimary: {
          backgroundColor: '#9D1500',
          '&:hover': {
            backgroundColor: '#7A1100',
          },
        },
        outlinedPrimary: {
          borderColor: '#D5D7DA',
          color: '#414651',
          '&:hover': {
            borderColor: '#9D1500',
            backgroundColor: 'rgba(157, 21, 0, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 1px 2px rgba(10, 13, 18, 0.05)',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 32,
          paddingRight: 32,
          '@media (min-width: 600px)': {
            paddingLeft: 32,
            paddingRight: 32,
          },
        },
      },
    },
  },
});

export default landingPageTheme;
